import {
  DayType,
  RecurrenceFormUnit,
  WeekDays,
  weekendDays,
  workWeek,
} from '@griply/types';

import { isNumber } from '../is-number';
import {
  ByWeekday,
  Frequency,
  hasOrphanBySetPos,
  Options,
  RRule,
} from '../lib/rrule';
import { getMonthDayNumbers } from './get-month-day-numbers';
import {
  getRruleWeekDays,
  parseByWeekdayWeekDays,
  parseRruleWeekDays,
} from './get-rrule-week-days';

export const parseIntervalFromRrule = (rrule: string) =>
  RRule.fromString(rrule).options.interval || 1;

// An unresolved weekDay leaves BYSETPOS without a BYDAY — an unsatisfiable, pathologically slow rule. Fall back to a fixed day-of-month.
const coerceOrphanBySetPos = (
  options: Partial<Options>,
  ordinal: number,
): Partial<Options> =>
  options.bysetpos != null && options.byweekday == null
    ? { ...options, bymonthday: [ordinal], bysetpos: undefined }
    : options;

// Read-only repair for legacy iOS orphan-BYSETPOS rules: reinterpret the position as a day-of-month. Valid rules pass through untouched.
export const coerceScheduleRrule = (
  format: string,
): { rrule: string; wasCoerced: boolean } => {
  if (!format) {
    return { rrule: format, wasCoerced: false };
  }

  const options = RRule.fromString(format).origOptions;
  if (!hasOrphanBySetPos(options)) {
    return { rrule: format, wasCoerced: false };
  }

  const bysetpos = Array.isArray(options.bysetpos)
    ? options.bysetpos
    : [options.bysetpos as number];

  const rrule = new RRule({
    ...options,
    bymonthday: bysetpos,
    bysetpos: undefined,
  }).toString();

  return { rrule, wasCoerced: true };
};

// All set*InRrule helpers clone `origOptions` before mutating. The cached
// RRule instance returned by `RRule.fromString` is shared across callers
// (see ../lib/rrule/rrule.ts); mutating it corrupts subsequent reads —
// notably `parseWeekDaysFromRrule` would then see numeric byweekday entries
// instead of Weekday objects, propagate undefined to the next setter, and
// crash inside the library's parseOptions on `.n of undefined`.

// The Days unit owns FREQ=DAILY (bare = "every 1 day" / every day, or
// FREQ=DAILY;INTERVAL=N = "every N days"); the interval picker spans 1..99 and
// changing it keeps the rule daily. The Weeks unit owns FREQ=WEEKLY;BYDAY=…
// (including all 7 days = "every day"), so "every day via weekday chips" no
// longer collapses to bare FREQ=DAILY (see setWeekDaysInRrule below). The two
// "every day" forms are equivalent at the schedule level; they only differ in
// which editor unit displays them.
export const setIntervalInRrule = (rrule: string, interval: number): string => {
  const newInterval = interval || 1;
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = { ...rule.origOptions };

  options.interval = newInterval > 1 ? newInterval : undefined;

  return new RRule(options).toString();
};

export const parseFormUnitFromRrule = (rrule: string = '') => {
  const rule = RRule.fromString(rrule);

  switch (rule.origOptions.freq) {
    case RRule.YEARLY:
      return RecurrenceFormUnit.Years;
    case RRule.MONTHLY:
      return RecurrenceFormUnit.Months;
    case RRule.DAILY:
      return RecurrenceFormUnit.Days;
    case RRule.WEEKLY:
    default:
      return RecurrenceFormUnit.Weeks;
  }
};

export const parseWeekDaysFromRrule = (rrule: string = ''): WeekDays[] => {
  const rule = RRule.fromString(rrule);
  if (rule.origOptions.freq === Frequency.DAILY) {
    return Object.values(WeekDays);
  }

  return Array.isArray(rule.origOptions.byweekday)
    ? parseRruleWeekDays(rule.origOptions.byweekday)
    : [];
};

export const setWeekDaysInRrule = (rrule: string, weekDays: WeekDays[]) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.WEEKLY,
    bymonthday: undefined,
    bysetpos: undefined,
    bymonth: undefined,
    byweekday: getRruleWeekDays(weekDays),
  };

  return new RRule(options).toString();
};

export const parseMonthDaysFromRrule = (rrule: string = ''): number[] => {
  const rule = RRule.fromString(rrule);

  if (isNumber(rule.origOptions.bymonthday)) {
    return (rule.origOptions.bymonthday as number) > 0
      ? [rule.origOptions.bymonthday as number]
      : [];
  }

  // Negative values (last day, second-to-last) are not grid days; keeping them
  // out prevents a later grid toggle from silently rewriting them away.
  return ((rule.origOptions.bymonthday as number[]) ?? []).filter(
    (day) => day > 0,
  );
};

export const setMonthDaysInRrule = (rrule: string, monthDays: number[]) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.MONTHLY,
    bymonthday: monthDays,
    bysetpos: undefined,
    byweekday: undefined,
  };

  return new RRule(options).toString();
};

export const parseMonthWeekDayFromRrule = (rrule: string = '') => {
  const rule = RRule.fromString(rrule);

  if (rule.origOptions.bymonthday === -1) {
    return {
      weekDay: DayType.Day,
      ordinal: (rule.origOptions.bysetpos as number) ?? -1,
    };
  }

  if (
    rule.origOptions.freq !== Frequency.MONTHLY ||
    !rule.origOptions.byweekday
  ) {
    return undefined;
  }

  const weekDays = Array.isArray(rule.origOptions.byweekday)
    ? rule.origOptions.byweekday
    : [];

  if (
    weekDays.length === workWeek.length &&
    parseRruleWeekDays(weekDays).every((weekDay) => workWeek.includes(weekDay))
  ) {
    return {
      weekDay: DayType.WorkDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  if (
    weekDays.length === weekendDays.length &&
    parseRruleWeekDays(weekDays).every((weekDay) =>
      weekendDays.includes(weekDay),
    )
  ) {
    return {
      weekDay: DayType.WeekendDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  const weekDay = weekDays[0] ?? rule.origOptions.byweekday;

  if (typeof weekDay !== 'object' || !('weekday' in weekDay)) {
    return undefined;
  }

  // Prefer bysetpos; fallback to the Weekday.n value (e.g., BYDAY=+2MO form)
  let ordinal = rule.origOptions.bysetpos as number;
  if (!isNumber(ordinal) && 'n' in weekDay) {
    const n = (weekDay as unknown as { n?: number }).n;
    if (isNumber(n)) {
      ordinal = n as number;
    }
  }

  return {
    weekDay: parseByWeekdayWeekDays([weekDay.weekday])[0],
    ordinal,
  };
};

export const setMonthSpecificDayInRrule = (
  rrule: string,
  {
    weekDay,
    ordinal,
  }: {
    weekDay: DayType | WeekDays;
    ordinal: number;
  },
) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.MONTHLY,
    bymonthday: undefined,
    byweekday: undefined,
    bysetpos: ordinal,
  };

  if (weekDay === DayType.Day) {
    options.bymonthday = [ordinal];
    options.bysetpos = undefined;
    return new RRule(options).toString();
  }

  options.byweekday = getRruleWeekDays([weekDay as WeekDays])?.[0];

  if (weekDay === DayType.WorkDay) {
    options.byweekday = getRruleWeekDays(workWeek);
  }

  if (weekDay === DayType.WeekendDay) {
    options.byweekday = getRruleWeekDays(weekendDays);
  }

  return new RRule(coerceOrphanBySetPos(options, ordinal)).toString();
};

export const parseYearWeekDayFromRrule = (rrule: string = '') => {
  const rule = RRule.fromString(rrule);

  const bymonthday = Array.isArray(rule.origOptions.bymonthday)
    ? rule.origOptions.bymonthday[0]
    : rule.origOptions.bymonthday;

  if (bymonthday === -1) {
    return {
      weekDay: DayType.Day,
      ordinal: (rule.origOptions.bysetpos as number) ?? -1,
    };
  }

  if (
    Frequency.YEARLY !== rule.origOptions.freq ||
    !rule.origOptions.byweekday
  ) {
    return undefined;
  }

  const weekDays = Array.isArray(rule.origOptions.byweekday)
    ? rule.origOptions.byweekday
    : [];

  if (
    weekDays.length === workWeek.length &&
    parseRruleWeekDays(weekDays).every((weekDay) => workWeek.includes(weekDay))
  ) {
    return {
      weekDay: DayType.WorkDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  if (
    weekDays.length === weekendDays.length &&
    parseRruleWeekDays(weekDays).every((weekDay) =>
      weekendDays.includes(weekDay),
    )
  ) {
    return {
      weekDay: DayType.WeekendDay,
      ordinal: rule.origOptions.bysetpos as number,
    };
  }

  const weekDay = weekDays[0] ?? rule.origOptions.byweekday;

  if (typeof weekDay !== 'object' || !('weekday' in weekDay)) {
    return undefined;
  }

  return {
    weekDay: parseByWeekdayWeekDays([weekDay.weekday])[0],
    ordinal: rule.origOptions.bysetpos as number,
  };
};

export const setYearSpecificMonthDayInRrule = (
  rrule: string,
  {
    weekDay,
    ordinal,
    month,
  }: {
    weekDay: DayType | WeekDays;
    ordinal: number;
    month: number;
  },
) => {
  const rule = RRule.fromString(rrule);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.YEARLY,
    bymonthday: undefined,
    byweekday: undefined,
    bysetpos: ordinal,
    bymonth: month,
  };

  if (weekDay === DayType.Day) {
    options.bymonthday = [ordinal];
    options.bysetpos = undefined;
    return new RRule(options).toString();
  }

  options.byweekday = getRruleWeekDays([weekDay as WeekDays])?.[0];

  if (weekDay === DayType.WorkDay) {
    options.byweekday = getRruleWeekDays(workWeek);
  }

  if (weekDay === DayType.WeekendDay) {
    options.byweekday = getRruleWeekDays(weekendDays);
  }

  return new RRule(coerceOrphanBySetPos(options, ordinal)).toString();
};

export const setYearMonthDaysInRrule = (
  rrule: string,
  monthDays: number[],
  month: number,
) => {
  const rule = RRule.fromString(rrule);
  const validDays = getMonthDayNumbers(month);
  const options: Partial<Options> = {
    ...rule.origOptions,
    freq: Frequency.YEARLY,
    bymonth: month,
    bymonthday: monthDays.filter((day) => validDays.includes(day)),
    bysetpos: undefined,
    byweekday: undefined,
  };

  return new RRule(options).toString();
};

export const parseMonthFromRrule = (rrule: string): number | undefined => {
  const rule = RRule.fromString(rrule);
  return rule.options.bymonth?.[0];
};

export const isValidRrule = (
  rrule: string = '',
  { hasCount }: { hasCount?: boolean } = {},
) => {
  if (!rrule) {
    return false;
  }

  const rule = RRule.fromString(rrule);
  if (
    ![
      Frequency.DAILY,
      Frequency.WEEKLY,
      Frequency.MONTHLY,
      Frequency.YEARLY,
    ].includes(rule.origOptions.freq!)
  ) {
    return false;
  }

  const hasNoOptions = [
    rule.origOptions.byweekday,
    rule.origOptions.bymonthday,
    rule.origOptions.bysetpos,
  ].every((option) => option === undefined);

  if (hasCount && hasNoOptions) {
    return true;
  }

  switch (rule.origOptions.freq) {
    case Frequency.DAILY:
      return hasNoOptions;
    case Frequency.WEEKLY:
      return !!rule.origOptions.byweekday;
    case Frequency.MONTHLY:
      return (
        !!rule.origOptions.bymonthday ||
        (!!rule.origOptions.byweekday &&
          (!!rule.origOptions.bysetpos ||
          Array.isArray(rule.origOptions.byweekday)
            ? 'n' in
              ((rule.origOptions.byweekday as Array<ByWeekday>)[0] as object)
            : 'n' in (rule.origOptions.byweekday as object)))
      );
    case Frequency.YEARLY:
      return (
        !!rule.origOptions.byweekday ||
        !!(rule.origOptions.bymonth && rule.origOptions.bymonthday)
      );
    default:
      return false;
  }
};
